import {
  LAYER_IMAGE_MIME_TYPES,
  LAYER_VIDEO_MIME_TYPES,
  LAYER_AUDIO_MIME_TYPES,
} from '@/lib/upload/acceptedMimeTypes';
import type {
  ImageLayer,
  VideoLayer,
  AudioLayer,
  TextLayer,
  TextLayerStyle,
} from './types';

export type AddableLayerType = 'image' | 'video' | 'audio' | 'text' | 'clip';

/** Per-type accept lists for the hidden file inputs and getUploadUrl. */
export const layerTypeConfig: Record<AddableLayerType, { acceptMimeTypes: readonly string[] }> = {
  image: { acceptMimeTypes: LAYER_IMAGE_MIME_TYPES },
  video: { acceptMimeTypes: LAYER_VIDEO_MIME_TYPES },
  audio: { acceptMimeTypes: LAYER_AUDIO_MIME_TYPES },
  text: { acceptMimeTypes: [] },
  clip: { acceptMimeTypes: [] },
};

export const TEXT_LAYER_DEFAULT_DURATION = 3;

export const SPEED_MIN = 0.25;
export const SPEED_MAX = 4;

export const FRAME_SCALE_MIN = 1;
export const FRAME_SCALE_MAX = 3;

/** fontName defaults to a built-in (AVAILABLE_FONTS in src/lib/get-font-data.ts),
 *  which needs no fontUrl — its id resolves to an absolute URL offline. */
export const DEFAULT_TEXT_STYLE: TextLayerStyle = {
  fontName: 'Montserrat Bold',
  fontUrl: null,
  fontSize: 64,
  color: '#ffffff',
  backgroundColor: null,
  backgroundOpacity: 1,
  borderRadius: 0,
  paddingX: 0,
  paddingY: 0,
  strokeColor: null,
  strokeWidth: 0,
  shadow: null,
  align: 'center',
  bold: false,
  italic: false,
};

export function makeImageLayer(
  { id, name, url, clipDuration }: { id: string; name: string; url: string; clipDuration: number },
): ImageLayer {
  return {
    id, type: 'image', url, name,
    start: 0, end: clipDuration || 5,
    rect: { x: 0.3, y: 0.3, w: 0.3, h: 0.3 },
    rotation: 0, opacity: 1, uploading: true,
  };
}

export function makeVideoLayer(
  { id, name, url, start, end, mediaDuration }:
  { id: string; name: string; url: string; start: number; end: number; mediaDuration: number },
): VideoLayer {
  return {
    id, type: 'video', source: 'upload', url, name, start, end,
    rect: { x: 0.25, y: 0.25, w: 0.5, h: 0.5 },
    rotation: 0, opacity: 1, uploading: true,
    volume: 1, mediaDuration,
    speed: 1, trimStart: 0, trimEnd: Math.min(mediaDuration, end - start),
  };
}

export function makeClipMirrorLayer(
  { id, name, start, end, clipDuration }:
  { id: string; name: string; start: number; end: number; clipDuration: number },
): VideoLayer {
  return {
    id, type: 'video', source: 'clip', url: '', name, start, end,
    rect: { x: 0.25, y: 0.25, w: 0.5, h: 0.5 },
    rotation: 0, opacity: 1, uploading: false,
    volume: 0, speed: 1, trimStart: 0, trimEnd: Math.max(0.1, end - start),
    mediaDuration: Math.max(0.1, clipDuration),
  };
}

export function makeAudioLayer(
  { id, name, url, start, end, mediaDuration }:
  { id: string; name: string; url: string; start: number; end: number; mediaDuration: number },
): AudioLayer {
  return {
    id, type: 'audio', url, name, start, end,
    opacity: 1, uploading: true, volume: 1, mediaDuration,
    speed: 1, trimStart: 0, trimEnd: Math.min(mediaDuration, end - start),
  };
}

export function makeTextLayer(
  { id, name, start, end }: { id: string; name: string; start: number; end: number },
): TextLayer {
  return {
    id, type: 'text', name, text: name, start, end,
    rect: { x: 0.1, y: 0.4, w: 0.8, h: 0.2 },
    rotation: 0, opacity: 1,
    style: { ...DEFAULT_TEXT_STYLE },
  };
}
